/**
 * Pure helpers for {@link SidebarRail}.
 */

import { PROVIDER_RAIL_KEYS, SHARED_RAIL_KEYS } from './SidebarRail.constants';

/**
 * Rail keys that stay PER-MEMBER ("individual") on a TEAM workspace. Product
 * decision: `automations` is per-member (each member runs their own automations
 * roster); `campaigns` and every other non-provider module are shared across the
 * team. Provider modules (email/calendar/drive) are always individual too — see
 * {@link isIndividualTeamRailKey}.
 */
export const INDIVIDUAL_ON_TEAM_RAIL_KEYS = new Set<string>(['automations']);

/** Individual (per-member) team-rail section: provider modules + {@link INDIVIDUAL_ON_TEAM_RAIL_KEYS}. Pure. */
export function isIndividualTeamRailKey(key: string): boolean {
  return PROVIDER_RAIL_KEYS.has(key) || INDIVIDUAL_ON_TEAM_RAIL_KEYS.has(key);
}

/**
 * Shared ("Team Modules") team-rail section: EVERY non-individual on-rail item —
 * the shared core keys PLUS any other granted, non-provider module (synthesis,
 * notes, places, and plugins like `campaigns`). Anything not classified as
 * individual lands here, so a granted entitlement-gated module can never fall
 * through the provider/shared gap and vanish entirely on a team workspace
 * (the team-visibility bug, #5455). Provider keys that are ALSO shared
 * (calendar/files) remain shared. Pure.
 */
export function isSharedTeamRailKey(key: string): boolean {
  if (INDIVIDUAL_ON_TEAM_RAIL_KEYS.has(key)) return false;
  return SHARED_RAIL_KEYS.has(key) || !PROVIDER_RAIL_KEYS.has(key);
}

/**
 * Legacy rail-key aliases — persisted `sidebar_settings` (sidebarVisibility +
 * sidebarOrder) written before a rail key was renamed. Maps old key → current
 * key so a user's saved show/hide + custom order survive the rename with no
 * config migration. (Agents module → Automations: rail key `agents` →
 * `automations`.)
 */
export const LEGACY_RAIL_KEY_ALIASES: Record<string, string> = { agents: 'automations' };

/**
 * Remap legacy rail keys in a persisted visibility map to their current key —
 * but only when the current key isn't already present, so an explicit new-key
 * value always wins over a stale legacy one. Pure.
 */
export function aliasLegacyRailVisibility(stored: Record<string, boolean>): Record<string, boolean> {
  let out = stored;
  for (const [oldKey, newKey] of Object.entries(LEGACY_RAIL_KEY_ALIASES)) {
    if (oldKey in out && !(newKey in out)) {
      out = { ...out, [newKey]: out[oldKey] };
      delete out[oldKey];
    }
  }
  return out;
}

/**
 * Remap legacy rail keys in a persisted order array to their current key,
 * de-duplicating (a legacy key and its current key collapse to one slot, the
 * earliest position wins). Pure.
 */
export function aliasLegacyRailOrder(order: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const k of order) {
    const mapped = LEGACY_RAIL_KEY_ALIASES[k] ?? k;
    if (!seen.has(mapped)) {
      seen.add(mapped);
      out.push(mapped);
    }
  }
  return out;
}

/** Calculate position-aware animation distance for module visibility toggle.
 * @returns Distance value to use in animation, or null if animation should be skipped.
 */
export function calculateToggleAnimDistance(
  toggledKey: string,
  direction: 'up' | 'down',
  visibility: Record<string, boolean>,
  allKeysInOrder: string[],
): number | null {
  const toggledIndex = allKeysInOrder.indexOf(toggledKey);

  if (direction === 'up') {
    // Module being enabled: slides up from off-section to its position.
    // Distance = how many visible modules come after it (+ 1 for the item itself).
    const visibleAfterToggled = allKeysInOrder
      .slice(toggledIndex + 1)
      .filter((k) => visibility[k] !== false).length;

    if (visibleAfterToggled === 0) {
      // #1868: Module lands at the bottom — no animation, appear in place.
      return null;
    }

    return Math.max(2, visibleAfterToggled + 1);
  } else {
    // Module being disabled: slides down to off-section.
    // Distance = how many visible modules come before it (+ 1 for movement to off-section).
    const visibleBeforeToggled = allKeysInOrder
      .slice(0, toggledIndex)
      .filter((k) => visibility[k] !== false).length;
    return Math.max(2, visibleBeforeToggled + 1);
  }
}

/**
 * Whether a workspace module should surface as a primary rail item.
 *
 * Detected via the explicit `isPrimaryRail` flag on the module (clean model),
 * or `pinned: true` in the user's ModuleSidebarPreference (seeder transition
 * signal via /module-preferences). The legacy archetype-label fallback was
 * removed in #3235 once seeded data carried the flag/pin signals.
 *
 * NOTE: the API serializes this field in camelCase (`isPrimaryRail`) via
 * `mapModuleEntity`; reading `is_primary_rail` (snake_case) here would always
 * be `undefined` at runtime and silently disable the flag path.
 *
 * Archived modules are never primary-rail items.
 */
export function isPrimaryRailModule(
  module: { id: string; archivedAt?: string; isPrimaryRail?: boolean },
  pinnedModuleIds: ReadonlySet<string>,
): boolean {
  if (module.archivedAt) return false;
  const hasPrimaryFlag = module.isPrimaryRail === true;
  const isPinned = pinnedModuleIds.has(module.id);
  return hasPrimaryFlag || isPinned;
}

/**
 * Sort visible rail items by a saved key order (Teams "move" mode). Keys absent
 * from `order` keep their original relative position (sorted after the ordered
 * ones). An empty `order` is a no-op (returns the input order). Pure — does not
 * mutate `items`.
 */
export function sortRailItemsByOrder<T extends { key: string }>(
  items: T[],
  order: readonly string[],
): T[] {
  if (!order.length) return items;
  return [...items].sort((a, b) => {
    const ra = order.indexOf(a.key);
    const rb = order.indexOf(b.key);
    return (
      (ra === -1 ? Number.MAX_SAFE_INTEGER : ra) -
      (rb === -1 ? Number.MAX_SAFE_INTEGER : rb)
    );
  });
}

/**
 * Merge a reordered list of CURRENTLY-VISIBLE category keys back together with
 * the previously-saved order, re-inserting any saved keys missing from the new
 * list (e.g. a category hidden in manager mode) at their previous index. This is
 * what stops a reorder from wiping a hidden category's saved slot (#3741). Pure.
 */
export function mergeRailOrder(
  orderedKeys: string[],
  prevOrder: readonly string[],
): string[] {
  const mergedOrder = [...orderedKeys];
  prevOrder.forEach((key, idx) => {
    if (!mergedOrder.includes(key)) {
      mergedOrder.splice(Math.min(idx, mergedOrder.length), 0, key);
    }
  });
  return mergedOrder;
}

/**
 * Build a {otherUserId -> unreadCount} map from the DM channels exposed by
 * NotificationsProvider (#4894). DM channel names are `userA+userB`; the "other"
 * id is whichever participant is not the current user. Only positive counts are
 * recorded (zero/undefined are omitted) so the rail's badge stays off for read
 * conversations. Pure.
 */
export function dmUnreadByUserId(
  dms: ReadonlyArray<{ name: string; unreadCount?: number }> | null,
  currentUserId: string,
): Record<string, number> {
  const result: Record<string, number> = {};
  if (!dms) return result;
  for (const dm of dms) {
    const otherId = dm.name.split('+').find((id) => id !== currentUserId);
    if (otherId && dm.unreadCount) result[otherId] = dm.unreadCount;
  }
  return result;
}

/**
 * Reverse the rail-key → visibility-key map: given a visibility key, return the
 * rail key that maps to it (drive↔files is the only remap in practice), falling
 * back to the visibility key itself when no rail key remaps to it (identity).
 * Pure. Centralizes the `Object.entries(map).find(([, v]) => v === visKey)?.[0]
 * ?? visKey` reverse-lookup that the on-list, off-list, and entitlement filter
 * all share, so the drive↔files invariant lives in one place.
 */
export function visibilityKeyToRailKey(
  visKey: string,
  railKeyToVisibilityKey: Record<string, string>,
): string {
  return Object.entries(railKeyToVisibilityKey).find(([, v]) => v === visKey)?.[0] ?? visKey;
}

/**
 * The visibility keys for the manage menu's OFF section — every default-known
 * module that is currently toggled off AND still entitled (default-deny: an
 * ungranted rail key is not offered as a re-enable affordance either, ADR 042
 * §13). The visibility key is mapped back to its rail key (drive↔files is the
 * only remap) before the entitlement check, mirroring the on-list. Pure;
 * extracted verbatim from SidebarRail.tsx so the membership filter is unit-
 * testable (#4963 regression: a presentation change must not surface a hidden
 * module). The caller still applies its stamp-order sort to the result.
 */
export function computeOffManageKeys(params: {
  allVisibilityKeys: string[];
  visibility: Record<string, boolean>;
  railKeyToVisibilityKey: Record<string, string>;
  isRailKeyHidden: (railKey: string) => boolean;
}): string[] {
  const { allVisibilityKeys, visibility, railKeyToVisibilityKey, isRailKeyHidden } = params;
  return allVisibilityKeys.filter((key) => {
    if (visibility[key] !== false) return false;
    const railKey = visibilityKeyToRailKey(key, railKeyToVisibilityKey);
    return !isRailKeyHidden(railKey);
  });
}

/**
 * Class string for the rail's scrollable modules container. While manage mode
 * is open it carries `manager-mode` (styling) AND the trailing
 * `rail-manager-overlay` token — the latter is LOAD-BEARING for the
 * escape-layer contract (#5266): `useModulePaneEscapeClose`'s DOM-presence
 * guard matches `[class$="-overlay"]`, so the module-pane Escape handler
 * declines while manage mode owns Escape (SidebarRail's `useEscapeKey` →
 * closeManager). Without it, listener order makes the pane handler run first
 * and Escape closes/mis-navigates the pane instead of exiting manage mode.
 * No CSS targets the token (mobile-modals.css matches `-modal-overlay` only).
 * Pure.
 */
export function railModulesScrollClassName(isManagerOpen: boolean): string {
  return `rail-modules-scroll${isManagerOpen ? ' manager-mode rail-manager-overlay' : ''}`;
}

/**
 * Whether a click on the currently-highlighted rail item should toggle-CLOSE
 * the module pane (ADR 028). Close fires ONLY when the pane is CONFIRMED on
 * screen: visible, targeting a module, and ChatBox has reported that exact
 * module rendered. Any unconfirmed highlight — pane still opening/attaching,
 * ChatBox unmounted, or state drift — resolves as open-intent instead, so a
 * click can never be swallowed as a close against a pane that isn't showing
 * (the intermittent "sidebar click does nothing" bug). Pure.
 */
export function shouldToggleCloseOnRailClick(params: {
  railKey: string;
  activeRailKey: string | null;
  isPaneVisible: boolean;
  paneModuleId: string | null;
  renderedModuleId: string | null;
}): boolean {
  const { railKey, activeRailKey, isPaneVisible, paneModuleId, renderedModuleId } = params;
  if (activeRailKey !== railKey) return false;
  return (
    isPaneVisible
    && paneModuleId !== null
    && renderedModuleId === paneModuleId
  );
}
